import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft, Loader2, MessageCircle } from "lucide-react";
import { whatsappReceiptUrl } from "../lib/whatsapp";
import { CONTACT_NUMBER } from "../mock";
import { invoiceFromBooking } from "../lib/invoice";

const numberToWordsInr = (num) => {
  if (num === 0) return "Zero";
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => (n < 20 ? a[n] : b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : ""));
  const three = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return (h ? a[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
  };
  const n = Math.floor(num);
  const paise = Math.round((num - n) * 100);
  let s = "";
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n / 100000) % 100);
  const thou = Math.floor((n / 1000) % 100);
  const hund = n % 1000;
  if (crore) s += two(crore) + " Crore ";
  if (lakh) s += two(lakh) + " Lakh ";
  if (thou) s += two(thou) + " Thousand ";
  if (hund) s += three(hund);
  s = s.trim();
  if (paise) s += " and " + two(paise) + " Paise";
  return s + " Only";
};


const Receipt = () => {
  const { bookingId } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.getBooking(bookingId);
      setB(resp);
    } catch (e) {
      setError("Booking not found");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#c8102e]" size={28} /></div>;
  }
  if (error || !b) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-neutral-600">{error || "Booking not found"}</p>
        <a href="/admin/bookings" className="text-[#c8102e] underline">Back to Reservations Desk</a>
      </div>
    );
  }

  const inv = invoiceFromBooking(b);
  const amount = inv.total;
  const displayId = b.booking_id.replace(/^AIP-/, "");
  const receiptNo = `RCPT/${displayId}`;
  const hotelAddress = [b.hotel_address, b.hotel_city, b.hotel_state].filter((x, i) => x && (i === 0 || !(b.hotel_address || "").includes(x))).join(", ");
  const issuedAt = new Date();
  const fmtDate = (d) => d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  const inr = (n) => `${b.currency || "INR"} ${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-neutral-100 py-8 print:bg-white print:py-0">
      {/* Print controls */}
      <div className="max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
        <a href="/admin/bookings" className="inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-[#c8102e]">
          <ArrowLeft size={16} /> Back to Reservations Desk
        </a>
        <div className="flex gap-2">
          <a data-testid="receipt-whatsapp-btn" href={whatsappReceiptUrl(b)} target="_blank" rel="noreferrer" className="inline-flex items-center h-9 px-4 text-sm font-medium bg-[#25D366] hover:bg-[#1ebe5a] text-white transition-colors">
            <MessageCircle size={16} className="mr-2" /> Send on WhatsApp
          </a>
          <Button data-testid="receipt-print-btn" onClick={() => window.print()} className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none">
            <Printer size={16} className="mr-2" /> Print Receipt
          </Button>
        </div>
      </div>

      {/* Receipt paper */}
      <div className="receipt-paper max-w-3xl mx-auto bg-white border border-neutral-200 shadow-sm print:shadow-none print:border-0">
        {/* Header */}
        <div className="p-8 border-b-2 border-[#c8102e] flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 bg-[#c8102e] flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-2xl tracking-tight">{(b.hotel_name || "H").charAt(0)}</span>
            </div>
            <div>
              <div data-testid="receipt-hotel-name" className="text-2xl font-semibold tracking-tight text-neutral-900">{b.hotel_name}</div>
              <div data-testid="receipt-hotel-address" className="text-xs text-neutral-600 mt-1">{hotelAddress}</div>
              {b.hotel_phone && <div className="text-xs text-neutral-600">Tel: {b.hotel_phone}</div>}
              {b.hotel_gstin && <div data-testid="receipt-hotel-gstin" className="text-xs text-neutral-600">GSTIN: {b.hotel_gstin}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cash Receipt</div>
            <div className="text-lg font-serif font-medium mt-0.5">{receiptNo}</div>
            <div className="text-xs text-neutral-500 mt-1">Issued: {fmtDate(issuedAt)}</div>
            <div className={`inline-block mt-2 px-2 py-1 text-[10px] uppercase tracking-widest font-medium ${b.payment_status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-300" : "bg-amber-50 text-amber-700 border border-amber-300"}`}>
              {b.payment_status === "paid" ? "Paid · Cash Collected" : "Payment Pending"}
            </div>
          </div>
        </div>

        {/* Guest + Hotel */}
        <div className="p-8 grid grid-cols-2 gap-8 border-b border-neutral-200">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Guest Details</div>
            <div className="font-medium text-neutral-900">{b.guest_first_name} {b.guest_last_name}</div>
            <div className="text-sm text-neutral-700">{b.phone}</div>
            {b.email && <div className="text-sm text-neutral-700">{b.email}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Booking Details</div>
            <div className="text-sm text-neutral-700">Booking No: <span data-testid="receipt-booking-no" className="font-mono">{displayId}</span></div>
            <div className="text-sm text-neutral-700 capitalize">Status: {b.booking_status.replace("_", " ")}</div>
          </div>
        </div>

        {/* Stay */}
        <div className="p-8 grid grid-cols-5 gap-4 border-b border-neutral-200 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Room Category</div>
            <div data-testid="receipt-room-category" className="font-medium mt-1">{b.room_type}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Check-in</div>
            <div className="font-medium mt-1">{b.check_in}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Check-out</div>
            <div className="font-medium mt-1">{b.check_out}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total Stay</div>
            <div data-testid="receipt-total-stay" className="font-medium mt-1">{b.nights} Night{b.nights > 1 ? "s" : ""}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Guests / Rooms</div>
            <div className="font-medium mt-1">{b.guests} / {b.rooms}</div>
          </div>
        </div>

        {/* Line items */}
        <div className="p-8">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-300 text-left text-neutral-600">
                <th className="py-2 px-2 font-medium">Room Category</th>
                <th className="py-2 px-2 font-medium text-right">Rate / Night</th>
                <th className="py-2 px-2 font-medium text-center">Nights</th>
                <th className="py-2 px-2 font-medium text-right">Rate / Room</th>
                <th className="py-2 px-2 font-medium text-center">Rooms</th>
                <th className="py-2 px-2 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-neutral-100">
                <td className="py-3">
                  <div className="font-medium text-neutral-900">{b.room_type}</div>
                  <div className="text-xs text-neutral-500">HSN/SAC: 996311 — Room accommodation service</div>
                </td>
                <td data-testid="receipt-rate-per-night" className="py-3 text-right">{inr(inv.ratePerNight)}</td>
                <td className="py-3 text-center">{inv.nights}</td>
                <td data-testid="receipt-rate-per-room" className="py-3 text-right">{inr(inv.ratePerRoom)}</td>
                <td className="py-3 text-center">{inv.rooms}</td>
                <td className="py-3 text-right">{inr(inv.roomTotal)}</td>
              </tr>
            </tbody>
          </table>

          {inv.items.length > 0 && (
            <table data-testid="receipt-extras-table" className="w-full text-sm mt-6">
              <thead>
                <tr className="border-b border-neutral-300 text-left text-neutral-600">
                  <th className="py-2 px-2 font-medium w-12">#</th>
                  <th className="py-2 px-2 font-medium">Additional Services</th>
                  <th className="py-2 px-2 font-medium text-center">Qty</th>
                  <th className="py-2 px-2 font-medium text-right">Rate</th>
                  <th className="py-2 px-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {inv.items.map((e, i) => (
                  <tr key={i} data-testid={`receipt-extra-row-${i}`} className="border-b border-neutral-100">
                    <td className="py-2 px-2 text-neutral-500">{i + 1}</td>
                    <td className="py-2 px-2 text-neutral-900">{e.name}</td>
                    <td className="py-2 px-2 text-center">{e.qty}</td>
                    <td className="py-2 px-2 text-right">{inr(e.price)}</td>
                    <td className="py-2 px-2 text-right">{inr(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Totals */}
          <div className="mt-6 ml-auto max-w-sm text-sm">
            <Row testId="receipt-room-total" label="Room Charges" value={inr(inv.roomTotal)} />
            {inv.items.length > 0 && <Row testId="receipt-extras-total" label="Additional Services" value={inr(inv.extrasTotal)} />}
            {inv.items.length > 0 && <Row testId="receipt-subtotal" label="Subtotal" value={inr(inv.subtotal)} />}
            <Row testId="receipt-discount" label="Less: Discount" value={`− ${inr(inv.discount)}`} />
            <Row testId="receipt-taxable" label="Taxable Value" value={inr(inv.taxable)} />
            <Row testId="receipt-cgst" label={`CGST @ ${(inv.gstRate / 2).toFixed(1)}%`} value={inr(inv.cgst)} />
            <Row testId="receipt-sgst" label={`SGST @ ${(inv.gstRate / 2).toFixed(1)}%`} value={inr(inv.sgst)} />
            <Row testId="receipt-gross" label="Total Amount" value={inr(inv.gross)} />
            <Row testId="receipt-round-off" label="Round Off" value={`${inv.roundOff < 0 ? "− " : "+ "}${inr(Math.abs(inv.roundOff))}`} />
            <div className="border-t border-neutral-300 mt-2 pt-2 flex items-center justify-between">
              <span className="font-medium text-neutral-900 uppercase text-xs tracking-widest">Grand Total</span>
              <span data-testid="receipt-grand-total" className="font-serif text-xl font-semibold">{inr(amount)}</span>
            </div>
            <div className="mt-3 border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-2 text-xs">
              <span className="uppercase tracking-widest text-[10px]">{b.payment_method}</span>
              <span className="mx-1">·</span>
              {b.payment_status === "paid" ? `Received in ${b.payment_method} on ${fmtDate(issuedAt)}` : "Amount pending"}
            </div>
          </div>

          <div className="mt-6 text-xs text-neutral-700 leading-relaxed border-t border-neutral-200 pt-4">
            <div className="font-medium text-neutral-900 mb-1">Amount in words</div>
            <div className="italic">{numberToWordsInr(amount)}</div>
          </div>

          {b.notes && (
            <div className="mt-4 text-xs text-neutral-700">
              <div className="font-medium text-neutral-900 mb-1">Notes</div>
              <div>{b.notes}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-neutral-200 grid grid-cols-2 gap-8">
          <div className="text-xs text-neutral-600 leading-relaxed">
            <div className="font-medium text-neutral-900 mb-1">Terms & Conditions</div>
            <ul className="list-disc pl-4 space-y-1">
              <li>This is a computer-generated cash receipt and does not require a signature.</li>
              <li>Check-in from 2:00 PM · Check-out by 12:00 PM.</li>
              <li>All disputes subject to {b.hotel_city || "local"} jurisdiction.</li>
              <li>For assistance, call {b.hotel_phone || CONTACT_NUMBER}.</li>
            </ul>
          </div>
          <div className="flex flex-col items-end justify-end">
            <div className="h-12" />
            <div className="w-48 border-t border-neutral-400 pt-1 text-center text-xs text-neutral-600">
              For {b.hotel_name} · Authorised Signatory
            </div>
          </div>
        </div>

        <div className="bg-neutral-50 px-8 py-3 text-center text-[10px] uppercase tracking-widest text-neutral-500 border-t border-neutral-200">
          Thank you for staying at {b.hotel_name}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-neutral-600">{label}</span>
    <span data-testid={testId} className="text-neutral-900 font-medium">{value}</span>
  </div>
);

export default Receipt;
